import { Entity } from "ecsy";
import { Player } from "../components/player";
import Simplex from "../../../libs/engine/util/simplex";
import { Obstacle, ObstacleType } from "../components/obstacle";
import ObstacleContainer from "../components/obstacle-container";
import Disposer from "../components/disposer";
import { Sprite, Texture, Container } from "pixi.js";
import DisposableSystem from "@/game/libs/engine/root/disposable-system";
import { getLevel } from "@/game/skifree/difficulty";
import { AssetPack, getPack } from "@/game/libs/engine/loader/asset-loader";
import { createObstacleFlag } from "@/game/skifree/ski-factory";

export default class ObstacleCreationSystem extends DisposableSystem<any> {
  public assetPack: AssetPack = null as any;
  public over: Container = null as any;
  public under: Container = null as any;

  init() {
    this._obstacleMap = new Map<string, boolean>();
    this._simplex = new Simplex(356);
  }

  execute(delta: number) {
    var playerEntity: Entity = this.queries.player.results[0];
    var containerEntity: Entity = this.queries.container.results[0];
    if (playerEntity && containerEntity) {
      var player = playerEntity.getComponent(Player);
      var scanHalf = ObstacleCreationSystem.SCAN_WIDTH_HALF;
      var scanDistance = ObstacleCreationSystem.SCAN_DISTANCE;
      this.scanWorld(
        player.x - scanHalf,
        player.x + scanHalf,
        player.y + scanDistance,
        containerEntity.getComponent(ObstacleContainer)
      );
    }
  }

  scanWorld(
    startX: number,
    endX: number,
    y: number,
    container: ObstacleContainer
  ) {
    var tileWidth = ObstacleCreationSystem.TILE_WIDTH;

    while (startX < endX) {
      var tileX = Math.floor(startX / tileWidth);
      var tileY = Math.floor(y / tileWidth);

      var chance = 0.3 + getLevel(y) * 0.001;

      var obstacleType = this.getObstacleType(tileX, tileY, chance);
      if (obstacleType !== null) {
        var key = tileX + "," + tileY;
        if (!this._obstacleMap.has(key)) {
          this._obstacleMap.set(key, true);
          var sprite = this.createObsSprite(
            tileX,
            tileY,
            tileWidth,
            obstacleType
          );
          container.container.addChild(sprite);
          var e: Entity = (this as any).world.createEntity();
          e.addComponent(Obstacle, {
            x: sprite.x,
            y: sprite.y,
            width: sprite.width,
            height: sprite.height,
            radius: this.getRadius(obstacleType),
            type: obstacleType,
            sprite: sprite,
            over: this.over,
            under: this.under
          }).addComponent(Disposer, {
            dispose: this.disposeEntity.bind(
              null,
              key,
              this._obstacleMap,
              sprite,
              e
            )
          });
        }
      }
      startX += tileWidth;
    }
  }

  createObsSprite(
    tileX: number,
    tileY: number,
    tileWidth: number,
    type: ObstacleType
  ): Sprite {
    var sprite = this.makeSprite(type);
    sprite.anchor.x = 0.5;
    sprite.anchor.y = 0.5;

    var offsetX = Math.random() * (tileWidth - sprite.width * 0.7);
    var offsetY = Math.random() * (tileWidth - sprite.height * 0.7);
    sprite.x = tileX * tileWidth + offsetX;
    sprite.y = tileY * tileWidth + offsetY;

    return sprite;
  }

  makeSprite(type: ObstacleType): Sprite {
    var sprite: Sprite = new Sprite();

    switch (type) {
      case ObstacleType.BAGGAGE_1:
        sprite.texture = this.getTexture("EOTP_obstacle_baggage1");
        break;
      case ObstacleType.BAGGAGE_2:
        sprite.texture = this.getTexture("EOTP_obstacle_baggage2");
        break;
      case ObstacleType.BAGGAGE_3:
        sprite.texture = this.getTexture("EOTP_obstacle_baggage3");
        break;
      case ObstacleType.RED_FLAG:
        sprite = createObstacleFlag(getPack());
        break;
      case ObstacleType.SIGN:
        sprite.texture = this.getTexture("EOTP_obstacle_sign");
        break;
      case ObstacleType.TEXT_1:
        sprite.texture = this.getTexture("EOTP_obstacle_text1");
        break;
      case ObstacleType.TEXT_2:
        sprite.texture = this.getTexture("EOTP_obstacle_text2");
        break;
      case ObstacleType.TREE:
        sprite.texture = this.getTexture("EOTP_obstacle_tree");
        break;
    }
    sprite.anchor.x = 0.5;
    sprite.anchor.y = 0.5;

    return sprite;
  }

  getRadius(type: ObstacleType): number {
    switch (type) {
      case ObstacleType.BAGGAGE_1:
        return 11;
      case ObstacleType.BAGGAGE_2:
        return 11;
      case ObstacleType.BAGGAGE_3:
        return 11;
      case ObstacleType.RED_FLAG:
        return 6;
      case ObstacleType.SIGN:
        return 7;
      case ObstacleType.TEXT_1:
        return 13;
      case ObstacleType.TEXT_2:
        return 13;
      case ObstacleType.TREE:
        return 7;
    }

    return 10;
  }

  getTexture(name: string): Texture {
    return this.assetPack.texture[name] as Texture;
  }

  disposeEntity(
    key: string,
    obstacleMap: Map<string, boolean>,
    sprite: Sprite,
    entity: Entity
  ): void {
    sprite.parent.removeChild(sprite);
    obstacleMap.delete(key);
    entity.remove();
  }

  getObstacleType(
    tileX: number,
    tileY: number,
    window: number
  ): ObstacleType | null {
    var val = (this._simplex.get(tileX, tileY) + 1) / 2;
    var min = 1 - window;
    var slotVal = window / 8;
    if (val > this.getSlot(min, slotVal, 7)) return ObstacleType.BAGGAGE_1;
    if (val > this.getSlot(min, slotVal, 6)) return ObstacleType.BAGGAGE_2;
    if (val > this.getSlot(min, slotVal, 5)) return ObstacleType.BAGGAGE_3;
    if (val > this.getSlot(min, slotVal, 4)) return ObstacleType.RED_FLAG;
    if (val > this.getSlot(min, slotVal, 3)) return ObstacleType.SIGN;
    if (val > this.getSlot(min, slotVal, 2)) return ObstacleType.TEXT_1;
    if (val > this.getSlot(min, slotVal, 1)) return ObstacleType.TEXT_2;
    if (val > this.getSlot(min, slotVal, 0)) return ObstacleType.TREE;
    return null;
  }

  getSlot(min: number, slotVal: number, count: number): number {
    return min + slotVal * count;
  }

  public dispose() {}

  static SCAN_WIDTH_HALF = 2100;
  static SCAN_DISTANCE = 600;
  static TILE_WIDTH = 100;
  private _obstacleMap: Map<string, boolean> = new Map<string, boolean>();
  private _simplex: Simplex = new Simplex(356);
}

(ObstacleCreationSystem as any).queries = {
  player: {
    components: [Player]
  },
  container: {
    components: [ObstacleContainer]
  }
};
